
// Angular
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, DOCUMENT } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
// Service
import { ThemeModeService } from './layout/components/sidebar/theme-mode.service';
import { SplashScreenService } from './layout/components/splash-screen/splash-screen.service';
import { AuthService } from '@auth-service';
// Rxjs
import { filter, fromEvent, interval } from 'rxjs';
// Components
import { GoogleAnalyticsGTagComponent } from './core/components/google-analytics-gtag/google-analytics-gtag.component';
import { SplashScreenComponent } from './layout/components/splash-screen/splash-screen.component';

@Component({
  selector: 'body[app-root]',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterOutlet, GoogleAnalyticsGTagComponent, SplashScreenComponent]
})
export class AppComponent implements OnInit {

  // Private properties
  private readonly router = inject(Router);
  private modeService = inject(ThemeModeService);
  private splashScreenService = inject(SplashScreenService);
  private readonly swUpdate = inject(SwUpdate);
  private readonly authService = inject(AuthService);
  private document = inject<Document>(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private isUpdatePromptVisible = false;
  private isCheckingForUpdate = false;
  private isNewVersionAvailable = false;
  private lastUpdatePromptTime = 0;

  /**
   * Creates an instance of AppComponent.
   * @memberof AppComponent
   */
  constructor() {
    this.setFaviconBasedOnDomain();
    this.destroyRef.onDestroy(() => {
      this.authService.flushRoleAndPermissions();
    });
  }

  /**
   * On init
   * @memberof AppComponent
   */
  ngOnInit(): void {
    this.modeService.init();
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.splashScreenService.hide({ delay: 80, duration: 250 });
      window.scrollTo(0, 0);
    });
    if (this.swUpdate.isEnabled) {
      this.handleNewAppVersionUpdate();
    }
  }

  /**
   * Set favicon based on domain
   * @private
   * @memberof AppComponent
   */
  private setFaviconBasedOnDomain(): void {
    const hostname = window.location.hostname;
    const subdomain = this.getSubdomain(hostname);
    if (subdomain) {
      const faviconSrc = `assets/media/logos/${subdomain}/favicon.ico`;
      const favicon: HTMLLinkElement = this.document.querySelector('#appFavicon') as HTMLLinkElement;
      if (favicon) {
        favicon.setAttribute('href', faviconSrc);
      }
    }
  }

  /**
   * Get subdomain from hostname
   * @private
   * @param {string} hostname
   * @returns {string}
   * @memberof AppComponent
   */
  private getSubdomain(hostname: string): string {
    const parts = hostname.split('.');
    if (parts.length >= 3) {
      return parts[1];
    }
    return 'bildelsbasen';
  }

  /**
   * Handle application version updates and periodically check for new versions
   * @private
   * @memberof AppComponent
   */
  private handleNewAppVersionUpdate(): void {
    this.swUpdate.versionUpdates.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(event => {
      switch (event.type) {
        case 'VERSION_READY':
          this.isNewVersionAvailable = true;
          this.promptForAppUpdate();
          break;
        case 'VERSION_INSTALLATION_FAILED':
          console.error('Service Worker installation failed:', event.error);
          break;
      }
    });
    this.swUpdate.unrecoverable.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(event => {
      console.error('Unrecoverable service worker state:', event.reason);
      if (!this.isUpdatePromptVisible) {
        this.isUpdatePromptVisible = true;
        if (confirm('The application needs to reload to continue working correctly. Reload now?')) {
          window.location.reload();
        }
        this.isUpdatePromptVisible = false;
      }
    });
    // Check for updates every 30 minutes
    interval(30 * 60 * 1000).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.checkForAppUpdate();
    });
    // Check for updates when user returns to the tab
    fromEvent(this.document, 'visibilitychange').pipe(
      filter(() => this.document.visibilityState === 'visible'),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.checkForAppUpdate();
    });
    // Check for updates when service worker is ready
    navigator.serviceWorker.ready.then(() => {
      if (!this.destroyRef.destroyed) {
        this.checkForAppUpdate();
      }
    }).catch(error => {
      console.error('Error waiting for Service Worker:', error);
    });
  }

  /**
   * Prompt user to reload when a new application version is available
   * @private
   * @memberof AppComponent
   */
  private promptForAppUpdate(): void {
    if (this.isUpdatePromptVisible || !this.isNewVersionAvailable) return;
    // Remind the user at most once every 30 minutes
    if (Date.now() - this.lastUpdatePromptTime < 30 * 60 * 1000) return;
    this.isUpdatePromptVisible = true;
    this.lastUpdatePromptTime = Date.now();
    if (confirm('New version available. Load New Version?')) {
      window.location.reload();
    }
    this.isUpdatePromptVisible = false;
  }

  /**
   * Check for application updates and prompt if a ready version is available
   * @private
   * @returns {Promise<void>}
   * @memberof AppComponent
   */
  private async checkForAppUpdate(): Promise<void> {
    if (this.isCheckingForUpdate || this.destroyRef.destroyed) return;
    this.isCheckingForUpdate = true;
    try {
      await this.swUpdate.checkForUpdate();
      if (this.destroyRef.destroyed) return;
      if (this.isNewVersionAvailable) {
        this.promptForAppUpdate();
      }
    } catch (error) {
      console.error('Error checking for application updates', error);
    } finally {
      this.isCheckingForUpdate = false;
    }
  }

}
